// Auth token storage.
//
// Normal ops/portal tabs persist tokens in localStorage (shared across tabs).
// A "portal view" tab — opened by an admin via the View Portal button — marks
// itself with sessionStorage['pv'] and keeps its tokens in sessionStorage so it
// is isolated per-tab and never clobbers the admin's own localStorage session.

const isPortalViewTab = (): boolean => {
  try { return sessionStorage.getItem('pv') === '1'; } catch { return false; }
};

// ── Embedded preview (CR #127) ──────────────────────────────────────────────
// A portal preview rendered in an in-app WINDOW runs inside an iframe. Iframes
// share the tab's session/localStorage, so writing tokens there would hijack
// the admin's own session. Instead the preview keeps its tokens in module
// memory: each iframe has its own JS realm, so they're isolated by construction
// and vanish when the window closes. Never persisted.
let memoryTokens: { accessToken: string | null; refreshToken: string | null } | null = null;

export const useMemoryTokens = (accessToken: string, refreshToken: string): void => {
  memoryTokens = { accessToken, refreshToken };
};

const memoryStore: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = {
  getItem: (k: string) => (memoryTokens ? (memoryTokens[k as 'accessToken' | 'refreshToken'] ?? null) : null),
  setItem: (k: string, v: string) => { if (memoryTokens) memoryTokens[k as 'accessToken' | 'refreshToken'] = v; },
  removeItem: (k: string) => { if (memoryTokens) memoryTokens[k as 'accessToken' | 'refreshToken'] = null; },
};

// The active store for THIS tab (or this iframe realm).
const store = (): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> =>
  memoryTokens ? memoryStore : (isPortalViewTab() ? sessionStorage : localStorage);

export const saveTokens = (accessToken: string, refreshToken: string): void => {
  store().setItem('accessToken', accessToken);
  store().setItem('refreshToken', refreshToken);
};

export const setAccessToken = (accessToken: string): void => {
  store().setItem('accessToken', accessToken);
};

export const getAccessToken = (): string | null => {
  return store().getItem('accessToken');
};

export const getRefreshToken = (): string | null => {
  return store().getItem('refreshToken');
};

export const clearTokens = (): void => {
  // Only clear this tab's tokens; keep the 'pv' marker so a portal-view tab
  // stays portal-scoped (and never falls back to the admin's localStorage).
  store().removeItem('accessToken');
  store().removeItem('refreshToken');
};

export const isPortalViewSession = (): boolean => isPortalViewTab() || memoryTokens !== null;

/** True only inside an admin's embedded portal-preview window (#127). */
export const isEmbeddedPreview = (): boolean => memoryTokens !== null;

/** The admin's own token from the parent tab — same-origin, preview only.
 *  Lets the preview file change requests as the admin (not as the client). */
export const getParentAdminToken = (): string | null => {
  if (!isEmbeddedPreview()) return null;
  try { return window.parent?.localStorage?.getItem('accessToken') ?? null; } catch { return null; }
};
